import { SlackChannelGroup, UserCommunication } from '@this+that/models';
import { StoreApi } from 'zustand';

import { SlackCommunicationsState } from './types';
import { SlackMessageTypes } from '../../../models';
import { useIntegrationsStore } from '../Integrations';
import { sortByLastMessage } from '../utils';

type ZustandSet = StoreApi<SlackCommunicationsState>['setState'];
type ZustandGet = StoreApi<SlackCommunicationsState>['getState'];

const updateSlackGroupCommunications = (
  groups: SlackChannelGroup[],
  channelId: string,
  updater: (comms: UserCommunication[]) => UserCommunication[],
): SlackChannelGroup[] =>
  groups.map((group) =>
    group.channelId === channelId
      ? { ...group, communications: updater(group.communications) }
      : group,
  );

export const handleSetLoading = (set: ZustandSet, isLoading: boolean) => {
  set({ isLoading });
};

export const handleSaveSlackChannels = (
  set: ZustandSet,
  directs: Partial<SlackChannelGroup>[],
  channels: Partial<SlackChannelGroup>[],
  exclusiveStartKey: string | null,
) => {
  set((state) => {
    const normalize = (
      incoming: Partial<SlackChannelGroup>[],
      existing: SlackChannelGroup[],
    ): SlackChannelGroup[] => {
      return incoming.map((item) => {
        const previous = existing.find((e) => e.channelId === item.channelId);

        return {
          ...previous,
          ...item,
          communications: previous?.communications ?? [],
        } as SlackChannelGroup;
      });
    };

    const updatedDirects = normalize(directs, state.directs);
    const updatedChannels = normalize(channels, state.channels);

    return {
      directs: [...updatedDirects].sort(sortByLastMessage),
      channels: [...updatedChannels].sort(sortByLastMessage),
      startKey: exclusiveStartKey,
    };
  });
};

export const handleAddSlackCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  type: SlackMessageTypes,
  channelId: string,
  communication: UserCommunication,
  isWebSocket: boolean = false,
) => {
  const state = get();
  const groups = state[type];

  if (isWebSocket) {
    const isOwnMessage =
      communication.userId === communication.authorUserId ||
      communication.userEmailAddress === communication.authorEmail;
    if (isOwnMessage) {
      return;
    }
  }

  const channelExists = groups.some((group) => group.channelId === channelId);

  if (channelExists) {
    const updatedGroups = groups.map((group) =>
      group.channelId === channelId
        ? {
            ...group,
            communications: [communication, ...group.communications],
            lastMessage: communication,
          }
        : group,
    );
    set({
      [type]: updatedGroups.sort(sortByLastMessage),
    });
  } else {
    const slackWorkspaceId = communication.slackWorkspaceId || '';
    const slackIntegrations =
      useIntegrationsStore.getState().userSlackIntegrations;
    const workspace = slackIntegrations.find(
      (integration) => integration.workspaceId === slackWorkspaceId,
    );

    const newChannelGroup: SlackChannelGroup = {
      channelId: channelId,
      channelName: communication.channelName || channelId,
      workspaceId: slackWorkspaceId,
      workspaceName: workspace?.workspaceName || '',
      communications: [communication],
      lastMessage: communication,
      members: [],
    };

    const updatedGroups = [newChannelGroup, ...groups];
    set({
      [type]: updatedGroups.sort(sortByLastMessage),
    });
  }
};

export const handleGetSlackCommunicationById = (
  get: ZustandGet,
  type: SlackMessageTypes,
  channelId: string,
  messageId: string,
): UserCommunication | undefined => {
  const state = get();
  return state[type]
    .find((group) => group.channelId === channelId)
    ?.communications.find((comm) => comm.messageId === messageId);
};

export const handleUpdateSlackCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  type: SlackMessageTypes,
  channelId: string,
  messageId: string,
  updatedFields: Partial<UserCommunication>,
) => {
  const state = get();
  const updatedGroups = updateSlackGroupCommunications(
    state[type],
    channelId,
    (comms) =>
      comms.map((comm) =>
        comm.messageId === messageId ? { ...comm, ...updatedFields } : comm,
      ),
  );

  const groupsWithUpdatedLastMessage = updatedGroups.map((group) => {
    if (group.channelId === channelId && group.communications.length > 0) {
      const latestMessage = group.communications[0];
      if (latestMessage.messageId === messageId) {
        return {
          ...group,
          lastMessage: { ...latestMessage, ...updatedFields },
        };
      }
    }
    return group;
  });

  set({
    [type]: groupsWithUpdatedLastMessage.sort(sortByLastMessage),
  });
};

export const handleDeleteSlackCommunication = (
  set: ZustandSet,
  get: ZustandGet,
  type: SlackMessageTypes,
  channelId: string,
  messageId: string,
) => {
  const state = get();
  const updatedGroups = updateSlackGroupCommunications(
    state[type],
    channelId,
    (comms) => comms.filter((comm) => comm.messageId !== messageId),
  );

  const groupsWithUpdatedLastMessage = updatedGroups.map((group) => {
    if (group.channelId === channelId) {
      const newLastMessage =
        group.communications.length > 0 ? group.communications[0] : undefined;
      return { ...group, lastMessage: newLastMessage };
    }
    return group;
  });

  set({
    [type]: groupsWithUpdatedLastMessage.sort(sortByLastMessage),
  });
};

export const handleDeleteSlackCommunications = (
  set: ZustandSet,
  get: ZustandGet,
  type: SlackMessageTypes,
  channelId: string,
  messageIds: string[],
) => {
  const state = get();
  const group = state[type].find((g) => g.channelId === channelId);

  if (!group) {
    return;
  }

  const heldIds = new Set(
    messageIds.filter((messageId) =>
      group.communications.some((comm) => comm.messageId === messageId),
    ),
  );

  if (heldIds.size === 0) {
    return;
  }

  const updatedGroups = updateSlackGroupCommunications(
    state[type],
    channelId,
    (comms) => comms.filter((comm) => !heldIds.has(comm.messageId)),
  );

  const groupsWithUpdatedLastMessage = updatedGroups.map((updatedGroup) => {
    if (updatedGroup.channelId === channelId) {
      const newLastMessage =
        updatedGroup.communications.length > 0
          ? updatedGroup.communications[0]
          : undefined;
      return { ...updatedGroup, lastMessage: newLastMessage };
    }
    return updatedGroup;
  });

  set({
    [type]: groupsWithUpdatedLastMessage.sort(sortByLastMessage),
  });
};

export const handleSetThreadCommunications = (
  set: ZustandSet,
  get: ZustandGet,
  type: SlackMessageTypes,
  channelId: string,
  communications: UserCommunication[],
) => {
  const state = get();
  const updatedGroups = updateSlackGroupCommunications(
    state[type],
    channelId,
    () => communications,
  );

  const groupsWithUpdatedLastMessage = updatedGroups.map((group) => {
    if (group.channelId === channelId) {
      const newLastMessage =
        communications.length > 0 ? communications[0] : undefined;
      return { ...group, lastMessage: newLastMessage };
    }
    return group;
  });

  set({
    [type]: groupsWithUpdatedLastMessage.sort(sortByLastMessage),
  });
};

export const handleClearSlackCommunicationsByWorkspaceId = (
  set: ZustandSet,
  workspaceId: string,
) => {
  set((state) => ({
    channels: state.channels.filter(
      (group) => group.workspaceId !== workspaceId,
    ),
    directs: state.directs.filter((group) => group.workspaceId !== workspaceId),
  }));
};
